import axios from 'axios';
import { ICaasStudentsParams } from 'src/types/ICaasStudents';
import { ICaasStudentsResponse } from 'src/types/ICaasStudentsResponse';
import {
  ICancellationRequestDetailsData,
  ICancellationRequestDetailsResponse,
} from 'src/types/ICancellationRequestDetails';
import {
  IAcceptCancellationResponse,
  ICancelCoproducerResponse,
  ICreateCoproducerResponse,
  IGetCoproducerResponse,
  IGetProducerResponse,
  ISignCoproducerResponse,
  IValidateCoproducerPayload,
  ValidateCoproducerResponse,
} from 'src/types/ICoproducer';
import { ICreateNewCourseRequest } from 'src/types/ICreateNewCourseRequest';
import { ICreateNewCourseResponse } from 'src/types/ICreateNewCourseResponse';
import { ICreateTrackingParams } from 'src/types/ICreateTracking';
import { ICreateWebhookParams } from 'src/types/ICreateWebhook';
import { IExportCaasStudentsParams } from 'src/types/IExportCaasStudentsParams';
import { IGetFinancialStatement } from 'src/types/IFinancialStatementResponse';
import { IGetAllCoursesPlatformAPI } from 'src/types/IGetAllCoursesPlatformAPI';
import {
  IGetWithdrawComposition,
  IWithdrawComposition,
} from 'src/types/IWithdrawComposition';
import {
  ICancellationCreateRequest,
  ICancellationCreateResponse,
  ICancellationDetailsData,
  ICancellationDetailsFullResponse,
  ICancellationDetailsResponse,
  ICancellationReasonsResponse,
  ICancellationSummaryRequest,
  ICancellationValues,
  IGetCancellationDetails,
} from 'src/types/IGetCancellationDetails';
import {
  ICancellationPagination,
  ICancellationRequest,
  ICancellationRequestsResponse,
  IGetCancellationRequests,
} from 'src/types/IGetCancellationRequests';
import {
  ICreateDocumentUploadUrlParams,
  IDocumentUploadUrl,
} from '~/types/IDocumentUploadUrl';
import { IGetConsolidatedReleases } from '~/types/iGetConsolidatedReleases';
import {
  IGetConsolidatedReleasesChartParams,
  IGetConsolidatedReleasesChartResponse,
} from '~/types/IGetConsolidatedReleasesChart';
import {
  IGetConversionChartParams,
  IGetConversionChartResponse,
} from '~/types/IGetConversionChart';
import { IGetEstimatedReleases } from '~/types/IGetEstimatedReleases';
import {
  IGetExclusiveSales,
  IGetExclusiveSalesResponse,
} from '~/types/IGetExclusiveSales';
import { IGetFinancialInstitutions } from '~/types/IGetFinancialInstitutions';
import { IGetFinancialStatementsParams } from '~/types/IGetFinancialStatementsParams';
import { IGetFinancialStatementSummary } from '~/types/IGetFinancialStatementSummary';
import { IGetProcessingReleases } from '~/types/IGetProcessingReleases';
import { IGetSellersChart } from '~/types/IGetSellersChartData';
import { IGetSellersChartParams } from '~/types/IGetSellersChartParams';
import { IGetStudentsInfoResponse } from '~/types/IGetStudentsInfoResponse';
import {
  IGetTrackingsResponse,
  ITrackingInstances,
} from '~/types/IGetTrackings';
import { IGetWebhookEventsResponse } from '~/types/IGetWebhookEvents';
import { IGetWebhooksResponse, IWebhookInstances } from '~/types/IGetWebhooks';
import { ILogin } from '~/types/ILogin';
import { ILoginResponse } from '~/types/ILoginResponse';
import { IRequestCsv } from '~/types/IRequestCsv';
import { IResetPassword } from '~/types/IResetPassword';
import { ISalesFunnelResponse } from '~/types/ISalesFunnelResponse';
import { ISendBankAccountParams } from '~/types/ISendBankAccountParams';
import { IStudentsInfoFilterParams } from '~/types/IStudentsInfoFilterParams';
import { IToggleTrackingStatusParams } from '~/types/IToggleTrackingStatusParams';
import { IToggleWebhookStatusParams } from '~/types/IToggleWebhookStatusParams';
import { IUpdateStudentCourseReleased } from '~/types/IUpdateStudentCourseReleased';
import { IUpdateTrackingParams } from '~/types/IUpdateTrackingParams';
import { IUpdateWebhookParams } from '~/types/IUpdateWebhookParams';

export const facebookMetaGraph = axios.create({
  baseURL: process.env.NEXT_PUBLIC_FACEBOOK_META_GRAPH_API,
  withCredentials: false,
});

export const platformAPI = axios.create({
  baseURL: process.env.NEXT_PUBLIC_PLATFORM_API_URL,
  withCredentials: false,
});

platformAPI.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

platformAPI.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export const createLogin = async (
  loginData: ILogin,
): Promise<ILoginResponse> => {
  const { data } = await platformAPI.post('/auth', loginData);

  return data;
};

export const createForgotPassword = async (email: string): Promise<void> => {
  const { data } = await platformAPI.post('/users/forgot_password', { email });

  return data;
};

export const createResetPassword = async (
  resetPasswordData: IResetPassword,
): Promise<void> => {
  const { data } = await platformAPI.post(
    '/users/reset_password',
    resetPasswordData,
  );

  return data;
};

export const getStudentsInfo = async (
  filterParams: IStudentsInfoFilterParams,
): Promise<IGetStudentsInfoResponse> => {
  const {
    page,
    limit,
    effectiveDates,
    status,
    productType,
    cancellations,
    chargebacks,
    key,
    search,
    delayedDays,
    delayedInstallments,
    currentInstallments,
    paidInstallments,
    signal,
    releaseDates,
    isCheckedClearRelease,
    courseIds,
    campaigns,
  } = filterParams;

  const formattedParams = {
    page,
    limit,
    status,
    productType,
    cancellations,
    chargebacks,
    courseId: courseIds,
    campaigns,
    delayedDays: [delayedDays],
    delayedInstallments: [delayedInstallments],
    currentInstallments: [currentInstallments],
    paidInstallments: [paidInstallments],
    ...(effectiveDates.every((date) => date != null)
      ? {
          startEffectiveDate: effectiveDates?.[0],
          endEffectiveDate: effectiveDates?.[1],
        }
      : {}),
    ...(isCheckedClearRelease
      ? {
          startReleaseDate: 'CLEAR',
          endReleaseDate: 'CLEAR',
        }
      : releaseDates.every((date) => date != null)
      ? {
          startReleaseDate: releaseDates?.[0],
          endReleaseDate: releaseDates?.[1],
        }
      : {}),

    ...(search.length ? { [key]: search } : {}),
  };

  const { data } = await platformAPI.get('/students', {
    params: formattedParams,
    signal,
  });

  return data;
};
export const updateStudentCourseReleased = async (
  id: number,
  date: string | null,
): Promise<IUpdateStudentCourseReleased> => {
  const { data } = await platformAPI.put(`/students/${id}`, {
    date,
  });
  return data;
};
export const createNewCourse = async (
  body: ICreateNewCourseRequest,
): Promise<ICreateNewCourseResponse> => {
  const { data } = await platformAPI.post('/courses', body);

  return data;
};

export const getSalesFunnel = async (
  CreditRequestId: string,
): Promise<ISalesFunnelResponse> => {
  const data = await platformAPI.get(`/sales/funnel/${CreditRequestId}`);

  return data;
};

export const getFinancialStatementSummary =
  async (): Promise<IGetFinancialStatementSummary> => {
    const { data } = await platformAPI.get('/financial-statement/summary');

    return data;
  };

export const getConsolidatedReleases = async ({
  page,
  limit,
  transactionType,
  selectedDates,
  signal,
}: IGetConsolidatedReleases): Promise<IGetFinancialStatement> => {
  const params = {
    page,
    limit,
    transactionType,
    selectedDates,
  };

  const { data } = await platformAPI.get(
    '/financial-statement/consolidated-releases',
    { params, signal },
  );

  return data;
};
export const getConsolidatedReleasesChart = async ({
  groupBy,
  signal,
}: IGetConsolidatedReleasesChartParams): Promise<IGetConsolidatedReleasesChartResponse> => {
  const { data } = await platformAPI.get(
    '/financial-statement/future-releases/consolidated',
    { params: { groupBy }, signal },
  );

  return data;
};

export const getFinancialStatement = async ({
  page,
  limit,
  from,
  to,
  releasedStatus,
  productTypes,
  types,
  transactionReferenceType,
  creditRequests,
  signal,
  order,
}: IGetFinancialStatementsParams): Promise<IGetFinancialStatement> => {
  const params = {
    page,
    limit,
    from,
    to,
    releasedStatus,
    productTypes,
    ...(types && { types: types }),
    transactionReferenceType,
    creditRequests,
    order,
  };

  const { data } = await platformAPI.get('/financial-statement', {
    params,
    signal,
  });
  return data;
};

export const exportFinancialStatement = async (params: IRequestCsv) => {
  const { data } = await platformAPI.post('/export/request', {
    ...params,
    filter: {
      ...params.filter,
      types:
        params.filter.types === 'all' || params.filter.types.length === 0
          ? undefined
          : params.filter.types,
    },
  });

  return data;
};

export const getWithdrawComposition = async (
  transactionV2Id: number,
  signal?: AbortSignal,
): Promise<IWithdrawComposition> => {
  const { data } = await platformAPI.get<IGetWithdrawComposition>(
    `/financial-statement/withdraw/${transactionV2Id}/composition`,
    { signal },
  );

  return data.data;
};

export const getProcessingReleases = async ({
  page,
  limit,
  selectedDates,
}: IGetProcessingReleases): Promise<IGetFinancialStatement> => {
  const params = {
    page,
    limit,
    selectedDates,
  };

  const { data } = await platformAPI.get(
    '/financial-statement/processing-releases',
    { params },
  );

  return data;
};

export const getEstimatedReleases = async ({
  page,
  limit,
  selectedDates,
}: IGetEstimatedReleases): Promise<IGetFinancialStatement> => {
  const params = {
    page,
    limit,
    selectedDates,
  };

  const { data } = await platformAPI.get(
    '/financial-statement/estimated-releases',
    { params },
  );

  return data;
};

export const getAllCourses = async () => {
  const { data } = await platformAPI.get<IGetAllCoursesPlatformAPI>(
    '/courses?listAll=true',
  );

  return data;
};

export const getSellersChart = async (
  {
    emails,
    productType,
    startEffectiveDate,
    endEffectiveDate,
  }: IGetSellersChartParams,
  signal?: AbortSignal,
) => {
  const { data } = await platformAPI.get<{ data: IGetSellersChart }>(
    '/sales/summary',
    {
      params: {
        ...(emails?.length ? { emails } : {}),
        ...(productType ? { productTypes: productType } : {}),
        ...(startEffectiveDate && endEffectiveDate
          ? { effectiveDates: [startEffectiveDate, endEffectiveDate] }
          : {}),
      },
      signal,
    },
  );

  data?.data?.data?.forEach((item) => {
    data?.data?.sellers?.forEach((seller) => {
      if (
        `${seller}|totalQty` in item === false ||
        `${seller}|totalValue` in item === false
      ) {
        item[`${seller}|totalQty`] = 0;
        item[`${seller}|totalValue`] = 0;
      }
    });
  });

  return data?.data;
};

export const getAllSellers = async (signal?: AbortSignal) => {
  const { data } = await platformAPI.get<{ data: string[] }>('/sales/sellers', {
    signal,
  });

  return data?.data;
};

export const getExclusiveSales = async ({
  page,
  status,
  cpf,
  signal,
}: IGetExclusiveSales) => {
  const { data } = await platformAPI.get<IGetExclusiveSalesResponse>(
    '/sales/exclusive',
    {
      params: {
        page,
        status,
        ...(cpf ? { CPF: cpf } : {}),
      },
      signal,
    },
  );

  return data;
};

export const getFinancialInstitutions =
  async (): Promise<IGetFinancialInstitutions> => {
    const { data } = await platformAPI.get('/bank-account/domain');

    return data;
  };

export const sendBankAccount = async (params: ISendBankAccountParams) => {
  const { data } = await platformAPI.put('/bank-account', params);

  return data;
};

export const getConversionChart = async (
  params: IGetConversionChartParams,
  signal?: AbortSignal,
): Promise<IGetConversionChartResponse> => {
  const { data } = await platformAPI.get('/conversion-dashboard', {
    params,
    signal,
  });

  return data;
};

export const getCaasStudents = async ({
  params,
  signal,
}: ICaasStudentsParams) => {
  const { data } = await platformAPI.get<ICaasStudentsResponse>(
    '/dynamic-product-dashboard',
    {
      params,
      signal,
    },
  );

  return data;
};

export const exportCaasStudentsInfo = async (
  params: IExportCaasStudentsParams,
): Promise<BlobPart> => {
  const { data } = await platformAPI.post(
    '/export/dynamic-product-dashboard',
    params,
  );

  return data;
};

export const getWebhooks = async () => {
  const { data } = await platformAPI.get<IGetWebhooksResponse>('/webhook');

  return data;
};

export const getWebhookEvents = async (signal?: AbortSignal) => {
  const { data } = await platformAPI.get<IGetWebhookEventsResponse>(
    '/webhook-events',
    {
      signal,
    },
  );

  return data;
};

export const createWebhook = async (params: ICreateWebhookParams) => {
  const { data } = await platformAPI.post<{ data: IWebhookInstances }>(
    '/webhook',
    params,
  );

  return data;
};

export const updateWebhook = async (params: IUpdateWebhookParams) => {
  const { data } = await platformAPI.patch(`/webhook/${params?.id}`, {
    configurationName: params.configurationName,
    enabledEvents: params.enabledEvents,
    webhookUrl: params.webhookUrl,
    authorizationToken: params?.authorizationToken || undefined,
  });

  return data;
};

export const toggleWebhookStatus = async ({
  id,
  active,
}: IToggleWebhookStatusParams) => {
  const { data } = await platformAPI.patch(`/webhook/${id}`, {
    active,
  });

  return data;
};

export const deleteWebhook = async (id: number) => {
  const { data } = await platformAPI.delete(`/webhook/${id}`);

  return data;
};

export const sendWebhookTest = async (webhookId: number) => {
  const { data } = await platformAPI.post(`/webhook/test/${webhookId}`);

  return data;
};

export const getTrackings = async (PartnerId: number) => {
  const { data } = await platformAPI.get<IGetTrackingsResponse>(
    `/tracking/${PartnerId}`,
  );
  return data;
};

/**
 * URL pré-assinada (POST) para subir um documento direto no bucket privado.
 * A key volta sempre no prefixo do parceiro logado.
 */
export const createDocumentUploadUrl = async (
  params: ICreateDocumentUploadUrlParams,
): Promise<IDocumentUploadUrl> => {
  const { data } = await platformAPI.post<{ data: IDocumentUploadUrl }>(
    '/documents/upload-url',
    params,
  );

  return data.data;
};

export const createTracking = async (params: ICreateTrackingParams) => {
  const { data } = await platformAPI.post<{ data: ITrackingInstances }>(
    '/tracking',
    params,
  );

  return data;
};

export const updateTracking = async (params: IUpdateTrackingParams) => {
  const { data } = await platformAPI.patch(`/tracking/${params?.id}`, {
    PartnerId: params?.PartnerId,
    type: params?.type,
    credentials: params?.credentials ?? undefined,
    active: params?.active,
    enabledEvents: params?.enabledEvents,
  });

  return data;
};

export const toggleTrackingStatus = async ({
  id,
  PartnerId,
  active,
}: IToggleTrackingStatusParams) => {
  const { data } = await platformAPI.patch(`/tracking/${id}`, {
    PartnerId,
    active,
  });

  return data;
};

export const deleteTracking = async ({ id, PartnerId }: any) => {
  const { data } = await platformAPI.delete(`/tracking/${id}`, {
    headers: {
      PartnerId,
    },
  });

  return data;
};

export const sendTrackingTest = async (trackingId: number) => {
  const { data } = await platformAPI.post(`/tracking/test/${trackingId}`);

  return data;
};

export const validateTrackingToken = async (token: string) => {
  const { data } = await facebookMetaGraph.get('/v16.0/me', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return data;
};

export const getCoproducer = async (): Promise<IGetCoproducerResponse> => {
  const { data } = await platformAPI.get<IGetCoproducerResponse>(
    '/coproducer/list-coproducer',
  );
  return data;
};

export const getProducer = async (): Promise<IGetProducerResponse> => {
  const { data } = await platformAPI.get<IGetProducerResponse>(
    '/coproducer/list-producers',
  );
  return data;
};

export const validateCoproducer = async (
  payload: IValidateCoproducerPayload,
): Promise<ValidateCoproducerResponse> => {
  const { externalPartnerId, cnpj, contractExpirationDate, splitRate } =
    payload || {};

  const { data } = await platformAPI.post<ValidateCoproducerResponse>(
    '/coproducer/validate',
    {
      externalPartnerId,
      cnpj,
      contractExpirationDate,
      splitRate,
    },
  );

  return data;
};

export const createCoproducer = async (
  payload: IValidateCoproducerPayload,
): Promise<ICreateCoproducerResponse> => {
  const { externalPartnerId, cnpj, contractExpirationDate, splitRate } =
    payload || {};

  const { data } = await platformAPI.post<ICreateCoproducerResponse>(
    '/coproducer',
    {
      externalPartnerId,
      cnpj,
      contractExpirationDate,
      splitRate,
    },
  );

  return data;
};

export const signCoproducer = async (
  PartnerCoproducerId: number,
): Promise<ISignCoproducerResponse> => {
  const { data } = await platformAPI.patch<ISignCoproducerResponse>(
    '/coproducer/sign',
    {
      PartnerCoproducerId,
    },
  );

  return data;
};

export const cancelCoproducer = async (
  PartnerCoproducerId: number,
): Promise<ICancelCoproducerResponse> => {
  const { data } = await platformAPI.patch<ICancelCoproducerResponse>(
    '/coproducer/cancel',
    { PartnerCoproducerId },
  );

  return data;
};

export const acceptCancellation = async (
  PartnerCoproducerId: number,
): Promise<IAcceptCancellationResponse> => {
  const { data } = await platformAPI.patch<IAcceptCancellationResponse>(
    '/coproducer/accept-cancellation',
    { PartnerCoproducerId },
  );

  return data;
};

export const getCancellationRequests = async ({
  page,
  limit,
  status,
  CreditRequestId,
  type,
  signal,
}: IGetCancellationRequests): Promise<ICancellationRequestsResponse> => {
  const mockCancellationRequests: ICancellationRequest[] = [
    {
      id: 1001,
      CreditRequestId: 5001,
      warrantyPeriod: 'in_warranty_period',
      status: 'pending',
      reason:
        'Desistiu pois precisa pagar uma parcela de outro contrato já existente',
      requestedAt: '2025-06-01T10:30:00Z',
      requestedBy: 'João Silva',
      cpf: '123.456.789-00',
    },
    {
      id: 1002,
      CreditRequestId: 5002,
      warrantyPeriod: 'not_applicable',
      status: 'canceled',
      reason: 'Está fora do mercado de trabalho',
      requestedAt: '2025-05-28T14:15:00Z',
      requestedBy: 'Maria Oliveira',
      cpf: '987.654.321-00',
    },
    {
      id: 1003,
      CreditRequestId: 5003,
      warrantyPeriod: 'in_warranty_period',
      status: 'done',
      reason: 'Desistiu pois não conseguiu acompanhar o curso',
      requestedAt: '2025-05-15T09:45:00Z',
      requestedBy: 'Carlos Pereira',
      cpf: '456.789.123-00',
    },
  ];

  // Filter mock data based on parameters
  let filteredRequests = mockCancellationRequests;

  // Filter by status
  if (status && status.length > 0) {
    const statusArray = Array.isArray(status) ? status : [status];
    filteredRequests = filteredRequests.filter((request) =>
      statusArray.includes(request.status),
    );
  }

  // Filter by CreditRequestId
  if (CreditRequestId) {
    filteredRequests = filteredRequests.filter(
      (request) => request.CreditRequestId === CreditRequestId,
    );
  }

  // Filter by warranty period type
  if (type && type.length > 0) {
    const typeArray = Array.isArray(type) ? type : [type];
    filteredRequests = filteredRequests.filter((request) =>
      typeArray.includes(request.warrantyPeriod),
    );
  }

  const mockPagination: ICancellationPagination = {
    itemsPerPage: limit || 50,
    currentPage: page || 1,
    totalItems: filteredRequests.length,
    totalPages: Math.ceil(filteredRequests.length / (limit || 50)),
    nextPage: null,
    previousPage: null,
  };

  const mockResponse: ICancellationRequestsResponse = {
    data: {
      pagination: mockPagination,
      cancellationRequests: filteredRequests,
    },
  };

  // await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate network delay
  // return mockResponse;

  // todo: Commented out real API call - uncomment when backend is ready

  const { data } = await platformAPI.get<ICancellationRequestsResponse>(
    '/cancellation',
    {
      params: {
        page,
        limit,
        status,
        CreditRequestId,
        type,
      },
      signal,
    },
  );

  return data;
};

export const getCancellationDetails = async ({
  page,
  limit,
  name,
  cpf,
  email,
  CreditRequestId,
  signal,
}: IGetCancellationDetails): Promise<ICancellationDetailsResponse> => {
  const mockRequests = [
    {
      name: 'João Silva',
      CPF: '123.456.789-00',
      CreditRequestId: 5001,
    },
    {
      name: 'Maria Oliveira',
      CPF: '987.654.321-00',
      CreditRequestId: 5002,
    },
    {
      name: 'Carlos Pereira',
      CPF: '456.789.123-00',
      CreditRequestId: 5003,
    },
    {
      name: 'Ana Santos',
      CPF: '321.654.987-00',
      CreditRequestId: 5004,
    },
    {
      name: 'Pedro Costa',
      CPF: '654.321.789-00',
      CreditRequestId: 5005,
    },
  ];

  // Filter mock data based on search parameters
  let filteredRequests = mockRequests;

  if (name) {
    filteredRequests = filteredRequests.filter((request) =>
      request.name.toLowerCase().includes(name.toLowerCase()),
    );
  }

  if (cpf) {
    filteredRequests = filteredRequests.filter((request) =>
      request.CPF.replace(/\D/g, '').includes(cpf.replace(/\D/g, '')),
    );
  }

  if (CreditRequestId) {
    filteredRequests = filteredRequests.filter(
      (request) => request.CreditRequestId === CreditRequestId,
    );
  }

  const mockPagination = {
    itemsPerPage: 50,
    currentPage: page || 1,
    totalItems: filteredRequests.length,
    totalPages: Math.ceil(filteredRequests.length / (limit || 50)),
    nextPage: null,
    previousPage: null,
  };

  const mockResponse: ICancellationDetailsResponse = {
    data: {
      pagination: mockPagination,
      requests: filteredRequests,
    },
  };

  await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate network delay
  // return mockResponse;

  // todo: Commented out real API call - uncomment when backend is ready
  const { data } = await platformAPI.get<ICancellationDetailsResponse>(
    '/cancellation/details',
    {
      params: {
        page,
        limit,
        name,
        cpf,
        email,
        CreditRequestId,
      },
      signal,
    },
  );

  return data;
};

export const getCancellationDetailsByContractId = async (
  CreditRequestId: number,
  signal?: AbortSignal,
): Promise<ICancellationDetailsFullResponse> => {
  // TODO: Replace with real API call when backend is ready
  const mockData: ICancellationDetailsData = {
    id: null,
    cancellationStatus: null,
    cancellationType: null,
    name: 'João Silva',
    CPF: '123.456.789-00',
    birthDate: '1990-05-15',
    CreditRequestId: CreditRequestId,
    product: 'Curso de Desenvolvimento Web',
    email: 'joao.silva@email.com',
    phone: '(11) 98765-4321',
    productType: 'Crédito Educacional Antecipado',
    creditRequestStatus: 'Ativo',
    madeEffectiveAt: '2024-03-15',
    campaignName: 'Black Friday 2024',
    installments: 12,
    hasUpfront: true,
    commissionInPercentage: '0.2000',
    purchaseValueInCents: 500000, // R$ 5.000,00
    totalAmountPaidInCents: 150000, // R$ 1.500,00
    totalReceivedValueInCents: 125000, // R$ 1.250,00
    maximumFineValueInCents: 100000, // R$ 1.000,00
    pixQrCode: null,
    pixQrCodeText: null,
    cancellationValues: null,
    canApplyFineCancellation: true,
  };

  await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate network delay

  // return { data: mockData };

  // TODO: Uncomment when backend is ready
  const { data } = await platformAPI.get<ICancellationDetailsFullResponse>(
    `/cancellation/details/${CreditRequestId}`,
    { signal },
  );
  return data;
};

export const getCancellationRequestById = async (
  cancellationRequestId: number,
  signal?: AbortSignal,
): Promise<ICancellationRequestDetailsResponse> => {
  // TODO: Replace with real API call when backend is ready
  const mockData: ICancellationRequestDetailsData = {
    id: cancellationRequestId,
    cancellationStatus: 'pending',
    cancellationType: 'with_fine',
    name: 'Renato Bertoline',
    CPF: '123.345.567-89',
    birthDate: '1992-12-12',
    CreditRequestId: 123456789,
    product: 'Nome do produto',
    email: 'joaosantos@gmail.com',
    phone: '(11) 99999-9999',
    productType: 'Crédito Educacional Dinâmico',
    creditRequestStatus: 'Efetivado',
    madeEffectiveAt: '2024-01-15',
    campaignName: 'Nome da campanha',
    installments: 25,
    hasUpfront: true,
    commissionInPercentage: '0.20000000',
    purchaseValueInCents: 199999, // R$ 1.999,99
    totalAmountPaidInCents: 21900, // R$ 219,00
    totalReceivedValueInCents: 11900, // R$ 119,00
    maximumFineValueInCents: 10000, // R$ 100,00
    pixQrCode: null,
    pixQrCodeText: null,
    refundValueInCents: null,
    fineValueInCents: 10000, // R$ 100,00
    warrantyPeriod: 'in_warranty_period',
    cancellationValues: {
      totalCancellationCost: 9100, // R$ 91,00
      availableBalanceInCents: 1500000, // R$ 15.000,00
      futureReleaseAmountCents: 25000, // R$ 250,00
      contractIOF: 12872, // R$ 128,72
      userRegistrationFeeInCents: 4820, // R$ 48,20
      commissionInCents: 23686, // R$ 236,86
      costOfCapital: 8000, // R$ 80,00
      repaymentOfDisbursement: 375960, // R$ 3759,60
      cancellationFixedCost: 112482, // R$ 1124,82
      hasFutureRelease: true,
      hasAvailableBalanceForCancellation: true,
    },
  };

  // await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate network delay

  // return { data: mockData };

  // TODO: Uncomment when backend is ready
  const { data } = await platformAPI.get<ICancellationRequestDetailsResponse>(
    `/cancellation/request/${cancellationRequestId}`,
    { signal },
  );
  return data;
};

export const calculateCancellationSummary = async (
  params: ICancellationSummaryRequest,
): Promise<ICancellationValues> => {
  // TODO: Replace with real API call when backend is ready
  console.log(params.fineValueInCents);

  // Different scenarios based on cancellation type and fine value
  let mockSummary: ICancellationValues;

  // Scenario 1: Negative total with sufficient balance (partner COM SALDO)
  if (
    params.cancellationType === 'with_fine' &&
    params.fineValueInCents &&
    params.fineValueInCents <= 5000
  ) {
    mockSummary = {
      totalCancellationCost: -9100, // R$ -91,00 (negative total)
      availableBalanceInCents: 1500000, // R$ 15.000,00 (sufficient balance)
      futureReleaseAmountCents: 25000, // R$ 250,00
      contractIOF: 12872, // R$ 128,72
      userRegistrationFeeInCents: 4820, // R$ 48,20
      commissionInCents: 23686, // R$ 236,86
      costOfCapital: 8000, // R$ 80,00
      repaymentOfDisbursement: 375960, // R$ 3759,60
      cancellationFixedCost: 112482, // R$ 1124,82
      hasFutureRelease: true,
      hasAvailableBalanceForCancellation: true,
    };
  }
  // Scenario 2: Negative total without sufficient balance (partner SEM SALDO)
  else if (
    params.cancellationType === 'with_fine' &&
    params.fineValueInCents &&
    params.fineValueInCents > 5000
  ) {
    mockSummary = {
      totalCancellationCost: -15000, // R$ -150,00 (negative total)
      availableBalanceInCents: 5000, // R$ 50,00 (insufficient balance)
      futureReleaseAmountCents: 25000, // R$ 250,00
      contractIOF: 12872, // R$ 128,72
      userRegistrationFeeInCents: 4820, // R$ 48,20
      commissionInCents: 23686, // R$ 236,86
      costOfCapital: 8000, // R$ 80,00
      repaymentOfDisbursement: 375960, // R$ 3759,60
      cancellationFixedCost: 112482, // R$ 1124,82
      hasFutureRelease: true,
      hasAvailableBalanceForCancellation: false,
    };
  }
  // Scenario 3: Positive total (standard case)
  else {
    mockSummary = {
      totalCancellationCost: 50000, // R$ 500,00 (positive)
      availableBalanceInCents: 75000, // R$ 750,00
      futureReleaseAmountCents: 25000, // R$ 250,00
      contractIOF: 5000, // R$ 50,00
      userRegistrationFeeInCents: 10000, // R$ 100,00
      commissionInCents: 15000, // R$ 150,00
      costOfCapital: 8000, // R$ 80,00
      repaymentOfDisbursement: 20000, // R$ 200,00
      cancellationFixedCost: 5000, // R$ 50,00
      hasFutureRelease: true,
      hasAvailableBalanceForCancellation: true,
    };
  }

  // await new Promise((resolve) => setTimeout(resolve, 800)); // Simulate network delay

  // return mockSummary;

  // TODO: Uncomment when backend is ready
  const { data } = await platformAPI.post<{ data: ICancellationValues }>(
    '/cancellation/details/summary',
    params,
  );
  return data?.data;
};

export const createCancellationRequest = async (
  params: ICancellationCreateRequest,
): Promise<ICancellationCreateResponse> => {
  // TODO: Replace with real API call when backend is ready
  const mockResponse: ICancellationCreateResponse = {
    data: {
      id: 12345,
      cancellationStatus: 'pending',
      cancellationType: params.cancellationType,
      pixQrCode:
        params.cancellationCompensationType === 'invoice'
          ? 'https://as1.ftcdn.net/v2/jpg/05/94/36/64/1000_F_594366491_I3vaOX6ZasBJsZNfuNErXASCcpcsQ1Co.jpg'
          : null,
      pixQrCodeText:
        params.cancellationCompensationType === 'invoice'
          ? '13241341414142gdtygdd6736g2gs26t6w261561g6562wv13241341414142gdtygdd6736g2gs26t6w261561g6562wv13241341414142gdtygdd6736g2gs26t6w261561g6562wv13241341414142gdtygdd6736g2gs26t6w261561g6.GOV.BCB.PIX'
          : null,
    },
  };

  // await new Promise((resolve) => setTimeout(resolve, 1200)); // Simulate network delay

  // return mockResponse;

  // TODO: Uncomment when backend is ready
  const { data } = await platformAPI.post<ICancellationCreateResponse>(
    '/cancellation',
    params,
  );
  return data;
};

export const getCancellationReasons =
  async (): Promise<ICancellationReasonsResponse> => {
    // TODO: Replace with real API call when backend is ready
    const mockReasons: ICancellationReasonsResponse = {
      data: {
        cancellationReasons: [
          {
            description: 'Motivos financeiros',
            subReasons: [
              { message: 'Optou por outra forma de pagamento' },
              { message: 'Dificuldades financeiras momentâneas' },
              { message: 'Não conseguiu pagar as parcelas' },
            ],
          },
          {
            description: 'Motivos pessoais',
            subReasons: [
              { message: 'Mudança de interesse profissional' },
              { message: 'Problemas de saúde' },
              { message: 'Mudança de cidade/país' },
            ],
          },
          {
            description: 'Motivos técnicos',
            subReasons: [
              { message: 'Problemas com a plataforma' },
              { message: 'Qualidade do conteúdo abaixo do esperado' },
              { message: 'Dificuldade de acompanhar o curso' },
            ],
          },
          {
            description: 'Outros motivos',
            subReasons: [],
          },
        ],
      },
    };

    // await new Promise((resolve) => setTimeout(resolve, 500)); // Simulate network delay
    // return mockReasons;

    // TODO: Uncomment when backend is ready
    const { data } = await platformAPI.get<ICancellationReasonsResponse>(
      '/cancellation/reasons',
    );
    return data;
  };
